import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  SimpleContact,
  contactsApi,
  defaultSearchQuery,
} from '@texas/api/endpoints/contactsApi';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import {
  VariantBranchSupplier,
  variantsApi,
} from '@texas/api/endpoints/variantsApi';
import { SupplierListItem } from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { inquiryEvents } from '@texas/components/article/variant/inquiry/events';
import { reactEvents } from '@bridge/reactEvents';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddVariantSupplierContactPanel } from './AddVariantSupplierContactPanel';
import { AddVariantSupplierOptionsPanel } from './AddVariantSupplierOptionsPanel';

interface AddVariantSupplierModalProps {
  supplier: SupplierListItem;
  variantId: number;
  branchId: number;
  branchName: string;
  branchIdentifier?: string;
  isOpen: boolean;
  onClose: () => void;
  onAdded?: (supplier: VariantBranchSupplier) => void;
  setIsAddingToBranch?: (id: number | null) => void;
}

export function AddVariantSupplierModal(props: AddVariantSupplierModalProps) {
  if (!props.isOpen) return null;

  return <AddVariantSupplierModalContent {...props} />;
}

function AddVariantSupplierModalContent({
  supplier,
  variantId,
  branchId,
  branchName,
  branchIdentifier,
  onClose,
  onAdded,
  setIsAddingToBranch,
}: AddVariantSupplierModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
  const [createdContacts, setCreatedContacts] = useState<SimpleContact[]>([]);

  const {
    data: contactsData,
    loading: contactsLoading,
    refetch: refetchContacts,
  } = useApiResource(contactsApi.getAll);
  const {
    data: options,
    loading: optionsLoading,
    refetch: refetchOptions,
  } = useApiResource(compositionGroupApi.optionsOverview);
  const {
    request: addSupplierRequest,
    error: addError,
    loading: isAdding,
  } = useApiRequest(variantsApi.addSupplierBranch);

  useEffect(() => {
    refetchContacts({
      ...defaultSearchQuery,
      supplierId: supplier.id,
      pageSize: 100,
    });
    refetchOptions({ variantId });
  }, [refetchContacts, refetchOptions, supplier.id, variantId]);

  useEffect(() => {
    if (contactsLoading || !contactsData) return;
    setSelectedContactIds(contactsData.items.map((contact) => contact.id));
  }, [contactsData, contactsLoading]);

  useEffect(() => {
    if (optionsLoading || !options) return;
    setSelectedOptionIds(
      options.filter((option) => option.isReady).map((option) => option.id),
    );
  }, [options, optionsLoading]);

  const contacts = useMemo(() => {
    const loadedContacts = contactsData?.items ?? [];
    return [
      ...loadedContacts,
      ...createdContacts.filter(
        (created) =>
          !loadedContacts.some((contact) => contact.id === created.id),
      ),
    ];
  }, [contactsData?.items, createdContacts]);

  const handleAdd = async () => {
    setIsAddingToBranch?.(branchId);
    const response = await addSupplierRequest(variantId, {
      branchId,
      supplierId: supplier.id,
      contactIds: selectedContactIds,
      optionIds: selectedOptionIds,
    });
    setIsAddingToBranch?.(null);

    if (response.hasError) return;

    reactEvents.variantSupplierChanged.dispatch();
    compositionGroupEvents.onCopy.dispatch();
    inquiryEvents.onVariantUpdated.dispatch(variantId);
    onAdded?.(response.data);
    toast({
      title: t('variant.supplier.addModal.added', {
        supplier: supplier.name,
      }),
      status: 'success',
      isClosable: true,
    });
    onClose();
  };

  const selectedOptionCount = selectedOptionIds.length;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      isCentered={true}
      scrollBehavior="inside"
      size="6xl"
      variant="entityDirectory"
    >
      <ModalOverlay />
      <ModalContent borderRadius={0} maxW="1080px" maxH="calc(100vh - 64px)">
        <ModalHeader px={8} pt={7} pb={6}>
          <Heading fontSize="3xl">{t('suppliers.addSupplier')}</Heading>
          <Text
            mt={2}
            color="entityDirectory.mutedText"
            fontFamily="instrumentSans"
            fontSize="sm"
            fontWeight="normal"
            textTransform="none"
          >
            {t('variant.supplier.addModal.description')}
          </Text>
        </ModalHeader>
        <ModalCloseButton
          border="1px solid"
          borderColor="entityDirectory.border"
          top={7}
          right={8}
        />

        <Flex
          align="center"
          justify="space-between"
          gap={6}
          px={8}
          py={4}
          bg="entityDirectory.raisedSurface"
          borderY="1px solid"
          borderColor="entityDirectory.border"
        >
          <Box minW={0}>
            <Text fontWeight="semibold" noOfLines={1}>
              {supplier.name}
            </Text>
            <Text color="entityDirectory.mutedText" fontSize="sm">
              {t('variant.supplier.addModal.newSupplierForArticle')}
            </Text>
          </Box>
          <Box textAlign="right" flexShrink={0}>
            <Text
              color="entityDirectory.mutedText"
              fontSize="2xs"
              letterSpacing="wider"
              textTransform="uppercase"
            >
              {t('variant.supplier.addModal.addingToBranch')}
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              {branchIdentifier
                ? `${branchIdentifier} · ${branchName}`
                : branchName}
            </Text>
          </Box>
        </Flex>

        <ModalBody p={0}>
          {addError && (
            <Box px={8} pt={6}>
              <ErrorDetails error={addError} />
            </Box>
          )}
          <Flex direction={{ base: 'column', lg: 'row' }} gap={8} px={8} py={7}>
            <AddVariantSupplierContactPanel
              supplierId={supplier.id}
              contacts={contacts}
              loading={contactsLoading}
              selectedIds={selectedContactIds}
              onChange={setSelectedContactIds}
              onContactCreated={(contact) => {
                setCreatedContacts((current) => [...current, contact]);
                setSelectedContactIds((current) => [...current, contact.id]);
              }}
            />
            <Divider
              orientation="vertical"
              display={{ base: 'none', lg: 'block' }}
              h="auto"
              borderColor="entityDirectory.border"
            />
            <AddVariantSupplierOptionsPanel
              options={options ?? []}
              loading={optionsLoading}
              selectedIds={selectedOptionIds}
              onChange={setSelectedOptionIds}
            />
          </Flex>
        </ModalBody>

        <ModalFooter
          justifyContent="space-between"
          px={8}
          py={5}
          borderRadius={0}
        >
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              {[
                t('variant.supplier.addModal.contactSummary', {
                  count: selectedContactIds.length,
                }),
                t('variant.supplier.addModal.versionSummary', {
                  count: selectedOptionCount,
                }),
              ].join(' · ')}
            </Text>
            <Text color="entityDirectory.mutedText" fontSize="xs">
              {t('variant.supplier.addModal.summaryDescription', {
                supplier: supplier.name,
              })}
            </Text>
          </Box>
          <Flex gap={3}>
            <Button
              variant="entityDirectorySecondary"
              onClick={onClose}
              isDisabled={isAdding}
            >
              {t('general.cancel')}
            </Button>
            <Button
              variant="entityDirectoryPrimary"
              onClick={handleAdd}
              isLoading={isAdding}
            >
              {t('suppliers.addSupplier')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
